import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  getStateApi,
  getBusinessCategoryApi,
  getClassificationApi,
  getDocumentTypeApi,
  registerSupplierApi,
  updateSupplierApi,
  getSupplierApi,
  getSupplierAdminApi,
} from "services/supplierDiversity/supplierDiversity.thunks";
import { ERR_MSG } from "constants/labels";
import { alertActions, authActions } from "_store";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalPopup, AutocompleteInput } from "_components";
import { raphaelinfo } from "images";
import { supplierDiversityFormat } from "_utils/constant";
import FileUpload from "../components/FileUpload";
import { SupplierDiversityLabels } from "_utils/labels";
import { useSelector } from "react-redux";
import { MultiSelectInput } from "../components/MultiSelectInput";
import dayjs from "dayjs";
import CompanyInfoAccordion from "../components/CompanyInfoAccordion";
import { convertPayloadToFormData } from "../utils/convertPayloadToFormData";

export const SupplierCompanyInfo = ({ isAdmin = false }) => {
  const dispatch = useDispatch();
  const header = "Supplier Development";
  const navigate = useNavigate();
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [files, setFiles] = useState([]);
  const [tempFiles, setTempFiles] = useState([]);
  const [businessClassification, setBusinessClassification] = useState([]);
  const [businessCategory, setBusinessCategory] = useState([]);
  const [documentType, setDocumentType] = useState([]);
  const [sdState, setSdState] = useState([]);
  let existingFiles = [];
  const [isEditEmail, setIsEditEmail] = useState(false);
  const [isOpenUpdateEmail, setIsOpenUpdateEmail] = useState(false);
  const [expDate, setExpDate] = useState(null);
  const [certAgency, setCertAgency] = useState(0);
  const [stateCert, setStateCert] = useState(0);
  const [supplierInfo, setSupplierInfo] = useState({});
  const [formAction, setFormAction] = useState("ADD");
  const [tempData, setTempData] = useState({});
  const [selectedClassification, setSelectedClassification] = useState("");
  const { state: locationState } = useLocation();
  const { id: viewDetailsUserId } = locationState || {};
  const [isUploadValid, setIsUploadValid] = useState(false);
  const [isCertReplaced, setIsCertReplaced] = useState(false);
  const [fileRemoved, setFileRemoved] = useState(0);
  const authUserId = useSelector((x) => x.auth?.userId);
  const authUserEmail = useSelector(
    (x) => x.auth?.value?.Data?.UserDetails?.email
  );
  const logout = () => dispatch(authActions.logout());
  const handleOnChange = (event, newvalue) => {
    setSelectedDocumentType(newvalue);
  };
  const handleFileChange = (newFiles) => {
    setTempFiles(newFiles);
    if (formAction === "UPDATE") {
      setFiles(newFiles);
    }
  };

  const defaultValues = {
    UserID: isAdmin ? supplierInfo?.UserID : Number(authUserId),
    CompanyName: supplierInfo?.CompanyName,
    ContactPerson: "",
    Title: "",
    Address: "",
    City: "",
    State: "",
    CompanyWebsite: "",
    Email:
      !isAdmin && formAction === "ADD" && authUserEmail
        ? authUserEmail
        : supplierInfo?.Email || "",
    ZipCode: "",
    PhoneNumber: supplierInfo?.PhoneNumber,
    CellPhone: "",
    CategoryID: "",
    ClassificationID: "",
    ServicesProductsProvided: "",
    ExpiryDate: "",
    AgencyID: "",
    AgencyStateID: "",
    FileData: [],
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, dirtyFields },
    trigger,
    setValue,
    reset,
  } = useForm({
    mode: "onBlur",
    defaultValues,
  });

  const hasRequiredFiles =
    tempFiles.length > 0
      ? tempFiles.some((item) => item.DocumentTypeID === 4) &&
        tempFiles.some((item) => item.DocumentTypeID === 5)
      : files?.some((item) => item.DocumentTypeID === 4) &&
        files?.some((item) => item.DocumentTypeID === 5);

  const isFormValid = () => {
    const hasDirtyFields = Object.keys(dirtyFields).length > 0;
    if (formAction === "ADD") {
      return isValid && hasRequiredFiles && isUploadValid;
    } else {
      if (hasRequiredFiles) {
        return hasDirtyFields || isUploadValid;
      } else {
        return hasDirtyFields && hasRequiredFiles && isUploadValid;
      }
    }
  };

  const handleClassificationChange = (newValue) => {
    setSelectedClassification(newValue);
    setValue("ClassificationID", newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger("ClassificationID");
  };

  useEffect(() => {
    fetchBusinessCategory();
    fetchClassification();
    fetchDocumentType();
    fetchState();
    fetchSupplierInfo();
  }, []);

  useEffect(() => {
    if (!supplierInfo) return;

    reset({
      AdditionalID: supplierInfo.AdditionalID,
      UserID: isAdmin ? supplierInfo.UserID : Number(authUserId),
      CompanyName: supplierInfo.CompanyName || "",
      ContactPerson: supplierInfo.ContactPerson || "",
      Title: supplierInfo.Title || "",
      Address: supplierInfo.Address || "",
      State: supplierInfo.State || 0,
      CompanyWebsite: supplierInfo.CompanyWebsite || "",
      Email:
        !isAdmin && formAction === "ADD" && authUserEmail
          ? authUserEmail
          : supplierInfo.Email || "",
      ZipCode: supplierInfo.ZipCode || "",
      PhoneNumber: supplierInfo.PhoneNumber || "",
      CellPhone: supplierInfo.CellPhone || "",
      CategoryID: supplierInfo.CategoryID || 0,
      ClassificationID: supplierInfo.ClassificationID || [],
      ServicesProductsProvided: supplierInfo.ServicesProductsProvided || "",
      ExpiryDate: supplierInfo.ExpiryDate || new Date(),
      AgencyID: supplierInfo.AgencyID || 0,
      AgencyStateID: supplierInfo.AgencyStateID || 0,
      FileData: supplierInfo.FileData || [],
    });

    if (supplierInfo.ClassificationID) {
      const classificationValue = Array.isArray(supplierInfo.ClassificationID)
        ? supplierInfo.ClassificationID.join(",")
        : supplierInfo.ClassificationID;
      setSelectedClassification(classificationValue);
    }

    existingFiles = supplierInfo?.FileData || [];
    setFiles(supplierInfo?.FileData);
  }, [supplierInfo, reset]);

  const fetchSupplierInfo = async () => {
    try {
      if (isAdmin && !viewDetailsUserId) throw new Error(ERR_MSG["073"]);

      const res = isAdmin
        ? await dispatch(getSupplierAdminApi(viewDetailsUserId)).unwrap()
        : await dispatch(getSupplierApi()).unwrap();
      if (!res || res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message || ERR_MSG["073"],
            header: header,
          })
        );
        return;
      }
      setSupplierInfo(res?.Data);
      res?.Data?.AdditionalID !== null && setFormAction("UPDATE");
      setExpDate(res?.Data.ExpiryDate);
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchBusinessCategory = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getBusinessCategoryApi()).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const categories = res?.Data?.map((data) => {
        return { label: data?.CategoryName, value: data?.CategoryID };
      });
      setBusinessCategory(categories);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchClassification = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getClassificationApi()).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const classification = res?.Data?.map((data) => {
        return {
          label: data?.ClassificationName,
          value: data?.ClassificationID,
        };
      });
      setBusinessClassification(classification);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchDocumentType = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getDocumentTypeApi("SD")).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const documentType = res?.Data?.map((data) => {
        return { label: data?.DocumentName, value: data?.DocumentID };
      });
      setDocumentType(documentType);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchState = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getStateApi()).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const sdState = res?.Data?.map((data) => {
        return { label: data?.StateDescription, value: data?.StateId };
      });
      setSdState(sdState);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    await trigger(fieldName);
  };

  const classificationData = (data) => {
    const isArrayOfNumbers =
      Array.isArray(data) && data.every((item) => typeof item === "number");

    if (isArrayOfNumbers) {
      return data;
    } else {
      return data.split(",").map(Number);
    }
  };

  const normalizeDate = (date) => {
    if (!date) return null;
    return new Date(date).toISOString();
  };

  const onSubmit = async (data) => {
    try {
      dispatch(alertActions.clear());
      let res;
      let payload;

      if (formAction === "UPDATE") {
        payload = {
          AdditionalID: supplierInfo?.AdditionalID,
          UserID: supplierInfo?.UserID,
        };

        Object.keys(dirtyFields).forEach((fieldName) => {
          if (fieldName === "ClassificationID") {
            payload[fieldName] = classificationData(data[fieldName]);
          } else {
            payload[fieldName] = data[fieldName];
          }
        });

        if (!dayjs(expDate).isSame(dayjs(supplierInfo?.ExpiryDate), "day")) {
          payload.ExpiryDate = normalizeDate(expDate);
        }

        if (certAgency !== supplierInfo?.AgencyID) {
          payload.AgencyID = certAgency;
        }

        if (stateCert !== supplierInfo?.AgencyStateID) {
          payload.AgencyStateID = stateCert;
        }

        const updatedFiles = tempFiles.filter((temp) => {
          const old = files.find((f) => f.id === temp.id);
          return old && old.File !== temp.File;
        });

        if (updatedFiles.length) {
          payload.FileData = updatedFiles;
        }

        if (dirtyFields.Email) {
          setTempData(payload);
          setIsEditEmail(true);
          return;
        }

        const formData = convertPayloadToFormData(payload);
        res = await dispatch(updateSupplierApi(formData)).unwrap();
      } else {
        payload = {
          AdditionalID: supplierInfo?.AdditionalID,
          UserID: supplierInfo?.UserID,
          CompanyName: data.CompanyName,
          ContactPerson: data.ContactPerson,
          Title: data.Title,
          Address: data.Address,
          State: data.State,
          CompanyWebsite: data.CompanyWebsite || "",
          Email: data.Email,
          ZipCode: data.ZipCode,
          PhoneNumber: data.PhoneNumber,
          CellPhone: data.CellPhone,
          CategoryID: data.CategoryID,
          ClassificationID: classificationData(data.ClassificationID),
          ServicesProductsProvided: data.ServicesProductsProvided,
          ExpiryDate: expDate,
          AgencyID: certAgency,
          AgencyStateID: stateCert,
          FileData: tempFiles,
        };

        if (dirtyFields.Email) {
          setTempData(payload);
          setIsEditEmail(true);
          return;
        }

        const formData = convertPayloadToFormData(payload, true);
        res = await dispatch(registerSupplierApi(formData)).unwrap();
      }

      if (typeof res === "undefined") {
        throw new Error(ERR_MSG["073"]);
      }

      if (res?.Succeeded === false) {
        throw new Error(res?.Message);
      }

      dispatch(
        alertActions.success({
          message: res?.Message,
          header: header,
        })
      );
      fetchSupplierInfo();
      setSelectedDocumentType(null);
      setFileRemoved(0);
      setFiles([]);
      setTempFiles([]);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const onSubmitChangeEmail = async () => {
    try {
      dispatch(alertActions.clear());
      setIsEditEmail(false);
      const formData = convertPayloadToFormData(tempData, formAction === "ADD");
      await dispatch(
        formAction === "ADD"
          ? registerSupplierApi(formData)
          : updateSupplierApi(formData)
      ).unwrap();
      setIsOpenUpdateEmail(true);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Email Update Failed",
        })
      );
    }
  };

  const handleIsEditEmailClose = () => {
    setIsEditEmail(false);

    reset({
      AdditionalID: supplierInfo.AdditionalID,
      UserID: isAdmin ? supplierInfo.UserID : Number(authUserId),
      CompanyName: supplierInfo.CompanyName || "",
      ContactPerson: supplierInfo.ContactPerson || "",
      Title: supplierInfo.Title || "",
      Address: supplierInfo.Address || "",
      City: supplierInfo.City || "",
      State: supplierInfo.State || "",
      CompanyWebsite: supplierInfo.CompanyWebsite || "",
      Email:
        !isAdmin && formAction === "ADD" && authUserEmail
          ? authUserEmail
          : supplierInfo.Email || "",
      ZipCode: supplierInfo.ZipCode || "",
      PhoneNumber: supplierInfo.PhoneNumber || "",
      CellPhone: supplierInfo.CellPhone || "",
      CategoryID: supplierInfo.CategoryID || "",
      ClassificationID: supplierInfo.ClassificationID || "",
      ServicesProductsProvided: supplierInfo.ServicesProductsProvided || "",
      ExpiryDate: supplierInfo.ExpiryDate || "",
      AgencyID: supplierInfo.AgencyID || "",
      AgencyStateID: supplierInfo.AgencyID || "",
      FileData: supplierInfo.FileData || [],
    });
  };

  const handleUpdateEmailClose = () => {
    setIsOpenUpdateEmail(false);

    logout();
  };

  const handleCompanyNameChange = (e, field) => {
    const inputValue = e.target.value;
    const filteredValue = inputValue.replace(/[^A-Za-z0-9\s&\-.]/g, "");
    field.onChange(filteredValue);
  };

  const limit800 = (value) => {
    if (value.length > 800) {
      return value.slice(0, 800);
    }
    return value;
  };

  const handlePhoneFormat = (e, field) => {
    let digits = e.target.value.replace(/\D/g, "");
    digits = digits.slice(0, 10);
    let formatted = digits;
    if (digits.length > 3 && digits.length <= 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else if (digits.length > 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
        6
      )}`;
    }
    field.onChange(formatted);
  };

  return (
    <>
      <Typography component="div" className="MapCenterAccecss">
        <CompanyInfoAccordion
          dateCreated={supplierInfo?.DateCreated}
          lastUpdate={supplierInfo?.DateUpdated}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 12, md: 9 }}>
              <Typography
                className="Personal-Information-container"
                component="div"
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Grid container spacing={2}>
                      <Grid
                        size={{ xs: 12, sm: 6, md: 6 }}
                        className="Personal-Information"
                      >
                        <Typography component="div" className="mapcontainer">
                          <Box className="Mapcenterlist">
                            <Controller
                              name="CompanyName"
                              control={control}
                              rules={{
                                required: "Company Name is Required",
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  placeholder="Company Name*"
                                  disabled={isAdmin}
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  onChange={(e) =>
                                    handleCompanyNameChange(e, field)
                                  }
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />

                            <Controller
                              name="CategoryID"
                              control={control}
                              rules={{
                                required: "Business Category is required",
                              }}
                              render={({ field, fieldState }) => (
                                <Autocomplete
                                  {...field}
                                  disabled={isAdmin}
                                  options={businessCategory || []}
                                  value={
                                    (businessCategory || []).find(
                                      (opt) => opt.value === field.value
                                    ) || null
                                  }
                                  onChange={(_, newValue) => {
                                    const newVal = newValue
                                      ? newValue.value
                                      : null;
                                    field.onChange(newVal);
                                  }}
                                  getOptionLabel={(option) =>
                                    option?.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option?.value === value?.value
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Business Category*"
                                      variant="outlined"
                                      fullWidth
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message}
                                      margin="normal"
                                    />
                                  )}
                                />
                              )}
                            />

                            <Controller
                              name="ServicesProductsProvided"
                              control={control}
                              rules={{
                                required:
                                  "Services / Products Provided is required",
                                maxLength: {
                                  value: 800,
                                  message: "Maximum of 800 characters allowed",
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  placeholder="Services / Products Provided*"
                                  disabled={isAdmin}
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  onChange={(e) => {
                                    const capped = limit800(e.target.value);
                                    field.onChange(capped);
                                  }}
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />

                            <Controller
                              name="Title"
                              control={control}
                              rules={{
                                required: "Title is required",
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  placeholder="Title*"
                                  disabled={isAdmin}
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />

                            <Controller
                              name="PhoneNumber"
                              control={control}
                              rules={{
                                required: "Phone number is required",
                                pattern: {
                                  value: /^\d{3}-\d{3}-\d{4}$/,
                                  message: "Invalid phone number",
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  placeholder="Phone Number*"
                                  disabled={isAdmin}
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  onChange={(e) => handlePhoneFormat(e, field)}
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </Typography>
                      </Grid>

                      <Grid
                        size={{ xs: 12, sm: 6, md: 6 }}
                        className="Personal-Information"
                      >
                        <Typography component="div" className="mapcontainer">
                          <Box className="Mapcenterlistconatiner">
                            <Controller
                              name="CompanyWebsite"
                              control={control}
                              rules={{
                                pattern: {
                                  value:
                                    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
                                  message: "Invalid URL format",
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  disabled={isAdmin}
                                  placeholder="Company Website"
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />
                            <MultiSelectInput
                              id="classificationID"
                              control={control}
                              name="ClassificationID"
                              placeholder="Certification*"
                              options={businessClassification || []}
                              value={selectedClassification}
                              onChange={handleClassificationChange}
                              error={!!errors.ClassificationID}
                              helperText={errors.ClassificationID?.message}
                              handleBlur={handleBlur}
                              setValue={setValue}
                              trigger={trigger}
                              disabled={isAdmin}
                              required={true}
                              rules={{ required: "Certification is required" }}
                            />
                            <Controller
                              name="ContactPerson"
                              control={control}
                              rules={{
                                required: "Contact Person is required",
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  disabled={isAdmin}
                                  placeholder="Contact Person*"
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />
                            <Controller
                              name="Email"
                              control={control}
                              rules={{
                                required: "Email is required",
                                pattern: {
                                  value:
                                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                  message: "Invalid email address",
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  disabled={isAdmin}
                                  placeholder="Email*"
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />
                            <Controller
                              name="CellPhone"
                              control={control}
                              rules={{
                                required: "Mobile phone number is required",
                                pattern: {
                                  value: /^\d{3}-\d{3}-\d{4}$/,
                                  message: "Invalid mobile phone number",
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  disabled={isAdmin}
                                  placeholder="Mobile Phone Number*"
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                  onChange={(e) => handlePhoneFormat(e, field)}
                                  autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Controller
                      name="Address"
                      control={control}
                      rules={{
                        required: "Address is required",
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled={isAdmin}
                          placeholder="Address*"
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          margin="normal"
                          autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                        />
                      )}
                    />
                    <Grid container spacing={2}>
                      <Grid
                        size={{ xs: 12, sm: 12, md: 6 }}
                        className="Personal-Information marbottom0 selecticon CompanyDetails passwordcheck"
                      >
                        <Controller
                          name="State"
                          control={control}
                          rules={{
                            required: "State is required",
                          }}
                          render={({ field, fieldState }) => (
                            <Autocomplete
                              {...field}
                              disabled={isAdmin}
                              options={sdState || []}
                              value={
                                (sdState || []).find(
                                  (opt) => opt.value === field.value
                                ) || null
                              }
                              onChange={(_, newValue) => {
                                const newVal = newValue ? newValue.value : null;
                                field.onChange(newVal);
                              }}
                              getOptionLabel={(option) => option?.label || ""}
                              isOptionEqualToValue={(option, value) =>
                                option?.value === value?.value
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="State*"
                                  variant="outlined"
                                  fullWidth
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  margin="normal"
                                />
                              )}
                            />
                          )}
                        />
                      </Grid>
                      <Grid
                        size={{ xs: 12, sm: 12, md: 6 }}
                        className="Personal-Information marbottom0 selecticon CompanyDetails passwordcheck"
                      >
                        <Controller
                          name="ZipCode"
                          control={control}
                          rules={{
                            required: "Invalid zipcode",
                            maxLength: {
                              value: 5,
                              message: "Invalid zipcode",
                            },
                            minLength: {
                              value: 5,
                              message: "Invalid zipcode",
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              placeholder="ZIP Code*"
                              disabled={isAdmin}
                              variant="outlined"
                              fullWidth
                              size="small"
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              margin="normal"
                              onChange={(e) => {
                                let val = e.target.value;
                                val = val.replace(/[^0-9]/g, "");
                                if (val.length > 5) {
                                  val = val.slice(0, 5);
                                }
                                field.onChange(val);
                              }}
                              autoComplete="off"
                                  inputProps={{
                                    autoComplete: "new-password",
                                    "data-form-type": "other",
                                  }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Typography>
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 3 }}>
              <Grid item xs={12} sm={12} md={4}>
                <Typography
                  component="div"
                  className="UploadFiles-container mapcontainer  "
                >
                  {!isAdmin ? (
                    <>
                      <Typography
                        component="div"
                        className="Personal-Informationsheading "
                      >
                        <Typography component="h2" variant="h5">
                          Document Upload{" "}
                          <img src={raphaelinfo} alt="raphaelinfo"></img>
                        </Typography>
                      </Typography>
                      <Typography
                        component="div"
                        className="passwordcheck marbottom0 selecticon"
                      >
                        <AutocompleteInput
                          control={control}
                          name="DocumentTypeID"
                          label="Document Type"
                          options={documentType}
                          error={!!errors.documentType}
                          helperText={errors.documentType?.message}
                          handleBlur={handleBlur}
                          onChange={handleOnChange}
                        />
                      </Typography>
                    </>
                  ) : (
                    ""
                  )}
                  <FileUpload
                    initialFiles={files}
                    portalKey={"SD"}
                    selectedDocumentType={selectedDocumentType}
                    supportedFormats={supplierDiversityFormat}
                    documentTypes={documentType}
                    control={control}
                    errors={errors}
                    onFileChange={handleFileChange}
                    exsistingFiles={existingFiles}
                    action={formAction}
                    setExpDate={setExpDate}
                    setAgency={setCertAgency}
                    setStateCert={setStateCert}
                    agencyID={certAgency || supplierInfo?.AgencyID}
                    agencyStateID={stateCert || supplierInfo?.AgencyStateID}
                    expDate={expDate || supplierInfo?.ExpiryDate}
                    isUserAdmin={isAdmin}
                    setUploadFileValid={setIsUploadValid}
                    certReplaced={isCertReplaced}
                    setCertReplaced={setIsCertReplaced}
                    removedFile={fileRemoved}
                    setRemovedFile={setFileRemoved}
                  />
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container justifyContent="space-between" spacing={2}>
            <Grid
              size={{ xs: 12, sm: 12, md: 12 }}
              className="Personal-Information"
            >
              <Button
                variant="contained"
                color="red"
                className="cancelbutton"
                type="button"
                onClick={() => {
                  navigate("/dashboardSD");
                }}
              >
                {isAdmin ? "Back" : "Cancel"}
              </Button>
              {!isAdmin ? (
                <Button
                  type="submit"
                  variant="contained"
                  className="submitbutton"
                  color="primary"
                  disabled={!isFormValid()}
                >
                  {formAction === "ADD" ? "Submit" : "Update"}
                </Button>
              ) : (
                ""
              )}
            </Grid>
          </Grid>
        </form>
      </Typography>
      {isEditEmail ? (
        <ModalPopup
          header={SupplierDiversityLabels.header}
          message1={SupplierDiversityLabels.message1}
          btnPrimaryText={SupplierDiversityLabels.btnConfirm1}
          btnSecondaryText={SupplierDiversityLabels.btnCancel}
          handlePrimaryClick={onSubmitChangeEmail}
          handleSecondaryClick={handleIsEditEmailClose}
          hideBackdrop={false}
        />
      ) : (
        ""
      )}
      {isOpenUpdateEmail ? (
        <ModalPopup
          header={SupplierDiversityLabels.header}
          message1={SupplierDiversityLabels.message2}
          btnPrimaryText={SupplierDiversityLabels.btnConfirm2}
          handlePrimaryClick={handleUpdateEmailClose}
          hideBackdrop={false}
        />
      ) : (
        ""
      )}
    </>
  );
};
